// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file PowerSearchEditPopover.tsx
 * @input InternalConfig, PartialFilter
 * @output Edit popover content with field/operator selectors and value editor
 * @position Sub-component; consumed by PowerSearch
 *
 * SYNC: When modified, update:
 * - /packages/core/src/PowerSearch/index.ts
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Button } from "../Button/index.js";
import { Selector } from "../Selector/index.js";
import { HStack, VStack } from "../Stack/index.js";
import { Icon } from "../Icon/index.js";
import { TreeList } from "../TreeList/index.js";
import { useTranslator } from "../i18n/index.js";
import { isImeKeyEvent } from "../hooks/useFocusTrap.js";
import "../theme/tokens.stylex.js";
import { spacingVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { PowerSearchValueEditor } from "./PowerSearchValueEditor.js";
import { resolveOperatorLabel } from "./resolveOperatorLabel.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Editable filter state for recursive nesting
// =============================================================================

function initEditableFilter(config, filter) {
  const op = config.getOperator(filter.field, filter.operator);
  if (op?.value.type === 'nested' && filter.value?.type === 'nested') {
    return {
      field: filter.field,
      operator: filter.operator,
      value: filter.value,
      _subFilters: filter.value.value.map(f => initEditableFilter(config, f))
    };
  }
  return {
    field: filter.field,
    operator: filter.operator,
    value: filter.value
  };
}
function isEditableFilterComplete(config, ef) {
  if (!ef.field || !ef.operator) {
    return false;
  }
  const op = config.getOperator(ef.field, ef.operator);
  if (op?.value.type === 'nested') {
    const subs = ef._subFilters ?? [];
    return subs.length > 0 && subs.every(s => isEditableFilterComplete(config, s));
  }
  return ef.value != null;
}
function editableToCompleteFilter(config, ef) {
  if (!ef.operator) {
    return null;
  }
  const op = config.getOperator(ef.field, ef.operator);
  if (op?.value.type === 'nested') {
    const subs = (ef._subFilters ?? []).map(s => editableToCompleteFilter(config, s)).filter(s => s != null);
    return {
      field: ef.field,
      operator: ef.operator,
      value: {
        type: 'nested',
        value: subs
      }
    };
  }
  if (ef.value == null) {
    return null;
  }
  return {
    field: ef.field,
    operator: ef.operator,
    value: ef.value
  };
}
function updateAtPath(filters, path, updater) {
  const [idx, ...rest] = path;
  const next = [...filters];
  if (rest.length === 0) {
    next[idx] = updater(next[idx]);
  } else {
    const sf = next[idx];
    next[idx] = {
      ...sf,
      _subFilters: updateAtPath(sf._subFilters ?? [], rest, updater)
    };
  }
  return next;
}
function removeAtPath(filters, path) {
  const [idx, ...rest] = path;
  if (rest.length === 0) {
    return filters.filter((_, i) => i !== idx);
  }
  const next = [...filters];
  const sf = next[idx];
  next[idx] = {
    ...sf,
    _subFilters: removeAtPath(sf._subFilters ?? [], rest)
  };
  return next;
}
function addAtPath(filters, parentPath, newFilter) {
  if (parentPath.length === 0) {
    return [...filters, newFilter];
  }
  const [idx, ...rest] = parentPath;
  const next = [...filters];
  const sf = next[idx];
  next[idx] = {
    ...sf,
    _subFilters: addAtPath(sf._subFilters ?? [], rest, newFilter)
  };
  return next;
}

// =============================================================================
// Nested sub-filter row
// =============================================================================

function NestedSubFilterRow({
  config,
  subFilter,
  onChange,
  isReadOnly
}) {
  const t = useTranslator();
  const fieldOptions = useMemo(() => config.getVisibleFields().map(field => ({
    value: field.key,
    label: field.label
  })), [config]);
  const operatorOptions = useMemo(() => {
    const operators = config.getVisibleOperators(subFilter.field);
    return operators.map(op => ({
      value: op.key,
      label: resolveOperatorLabel(op, t)
    }));
  }, [config, subFilter.field, t]);
  const currentOperator = subFilter.operator ? config.getOperator(subFilter.field, subFilter.operator) : undefined;
  const operatorValue = currentOperator?.value;
  const isEmptyType = operatorValue?.type === 'empty';
  const isNestedType = operatorValue?.type === 'nested';
  const handleFieldChange = useCallback(fieldKey => {
    const defaultOp = config.getDefaultOperator(fieldKey);
    const newOp = defaultOp ? config.getOperator(fieldKey, defaultOp.key) : undefined;
    onChange({
      field: fieldKey,
      operator: defaultOp?.key,
      value: undefined,
      _subFilters: newOp?.value.type === 'nested' ? [] : undefined
    });
  }, [config, onChange]);
  const handleOperatorChange = useCallback(operatorKey => {
    const newOp = config.getOperator(subFilter.field, operatorKey);
    const oldOp = currentOperator;
    const keepValue = newOp && oldOp && newOp.value.type === oldOp.value.type;
    onChange({
      ...subFilter,
      operator: operatorKey,
      value: keepValue ? subFilter.value : undefined,
      _subFilters: newOp?.value.type === 'nested' ? subFilter._subFilters ?? [] : undefined
    });
  }, [config, subFilter, currentOperator, onChange]);
  const handleValueChange = useCallback(value => {
    onChange({
      ...subFilter,
      value
    });
  }, [subFilter, onChange]);
  return /*#__PURE__*/_jsxs(HStack, {
    gap: 2,
    vAlign: "center",
    children: [/*#__PURE__*/_jsx("div", {
      ...{
        className: "x2lah0s x1oysuqx"
      },
      children: /*#__PURE__*/_jsx(Selector, {
        label: t('@astryx.powersearch.editor.field'),
        isLabelHidden: true,
        options: fieldOptions,
        value: subFilter.field,
        onChange: handleFieldChange,
        isDisabled: isReadOnly,
        size: "md"
      })
    }), operatorOptions.length > 0 && /*#__PURE__*/_jsx("div", {
      ...{
        className: "x2lah0s xzjbwwf"
      },
      children: /*#__PURE__*/_jsx(Selector, {
        label: t('@astryx.powersearch.editor.operator'),
        isLabelHidden: true,
        options: operatorOptions,
        value: subFilter.operator,
        onChange: handleOperatorChange,
        isDisabled: isReadOnly,
        size: "md"
      })
    }), operatorValue && !isEmptyType && !isNestedType && /*#__PURE__*/_jsx("div", {
      ...{
        className: "xgyuaek xs83m0k xeuugli"
      },
      children: /*#__PURE__*/_jsx(PowerSearchValueEditor, {
        operatorValue: operatorValue,
        filterValue: subFilter.value,
        onChange: handleValueChange,
        config: config,
        isDisabled: isReadOnly
      })
    })]
  });
}

// =============================================================================
// Nested editor
// =============================================================================

function NestedEditor({
  config,
  partialFilter,
  operatorOptions,
  onOperatorChange,
  onPartialFilterChange,
  isReadOnly
}) {
  const t = useTranslator();
  const [subFilters, setSubFilters] = useState(() => {
    if (partialFilter.value && partialFilter.value.type === 'nested') {
      return partialFilter.value.value.map(f => initEditableFilter(config, f));
    }
    return [];
  });
  const syncToParent = useCallback(newSubFilters => {
    if (newSubFilters.length > 0 && newSubFilters.every(sf => isEditableFilterComplete(config, sf))) {
      onPartialFilterChange({
        ...partialFilter,
        value: {
          type: 'nested',
          value: newSubFilters.map(sf => editableToCompleteFilter(config, sf)).filter(sf => sf != null)
        }
      });
    } else {
      onPartialFilterChange({
        ...partialFilter,
        value: undefined
      });
    }
  }, [config, partialFilter, onPartialFilterChange]);
  const handleUpdate = useCallback((path, updated) => {
    setSubFilters(prev => {
      const next = updateAtPath(prev, path, () => updated);
      syncToParent(next);
      return next;
    });
  }, [syncToParent]);
  const handleRemove = useCallback(path => {
    setSubFilters(prev => {
      const next = removeAtPath(prev, path);
      syncToParent(next);
      return next;
    });
  }, [syncToParent]);
  const handleAdd = useCallback(parentPath => {
    const fields = config.getVisibleFields();
    const defaultField = fields[0];
    if (!defaultField) {
      return;
    }
    const defaultOp = config.getDefaultOperator(defaultField.key);
    const op = defaultOp ? config.getOperator(defaultField.key, defaultOp.key) : undefined;
    const newSubFilter = {
      field: defaultField.key,
      operator: defaultOp?.key,
      value: undefined,
      _subFilters: op?.value.type === 'nested' ? [] : undefined
    };
    setSubFilters(prev => {
      const next = addAtPath(prev, parentPath, newSubFilter);
      syncToParent(next);
      return next;
    });
  }, [config, syncToParent]);
  function buildTreeItems(filters, parentPath) {
    const items = filters.map((sf, idx) => {
      const itemPath = [...parentPath, idx];
      const op = sf.operator ? config.getOperator(sf.field, sf.operator) : undefined;
      const isNested = op?.value.type === 'nested';
      if (isNested) {
        const children = buildTreeItems(sf._subFilters ?? [], itemPath);
        if (!isReadOnly) {
          children.push({
            id: `${itemPath.join('-')}-add`,
            label: /*#__PURE__*/_jsx(Button, {
              label: t('@astryx.powersearch.editor.addFilter'),
              onClick: () => handleAdd(itemPath),
              variant: "ghost",
              size: "sm"
            })
          });
        }
        return {
          id: `filter-${itemPath.join('-')}`,
          label: /*#__PURE__*/_jsx(NestedSubFilterRow, {
            config: config,
            subFilter: sf,
            onChange: updated => handleUpdate(itemPath, updated),
            isReadOnly: isReadOnly
          }),
          isExpanded: true,
          children,
          endContent: !isReadOnly ? /*#__PURE__*/_jsx(Button, {
            label: t('@astryx.powersearch.editor.removeFilter'),
            icon: /*#__PURE__*/_jsx(Icon, {
              icon: "close",
              size: "sm"
            }),
            variant: "ghost",
            size: "sm",
            onClick: () => handleRemove(itemPath),
            isIconOnly: true
          }) : undefined
        };
      }
      return {
        id: `filter-${itemPath.join('-')}`,
        isExpanded: true,
        label: /*#__PURE__*/_jsx(NestedSubFilterRow, {
          config: config,
          subFilter: sf,
          onChange: updated => handleUpdate(itemPath, updated),
          isReadOnly: isReadOnly
        }),
        endContent: !isReadOnly ? /*#__PURE__*/_jsx(Button, {
          label: t('@astryx.powersearch.editor.removeFilter'),
          icon: /*#__PURE__*/_jsx(Icon, {
            icon: "close",
            size: "sm"
          }),
          variant: "ghost",
          size: "sm",
          onClick: () => handleRemove(itemPath),
          isIconOnly: true
        }) : undefined
      };
    });
    return items;
  }
  const treeChildren = buildTreeItems(subFilters, []);
  if (!isReadOnly) {
    treeChildren.push({
      id: 'add-filter',
      label: /*#__PURE__*/_jsx(Button, {
        label: t('@astryx.powersearch.editor.addFilter'),
        onClick: () => handleAdd([]),
        variant: "ghost",
        size: "sm"
      })
    });
  }
  const rootLabel = /*#__PURE__*/_jsx("div", {
    ...{
      className: "xcr08ib"
    },
    children: operatorOptions.length > 1 ? /*#__PURE__*/_jsx("div", {
      ...{
        className: "x1iyjqo2 x2lah0s"
      },
      children: /*#__PURE__*/_jsx(Selector, {
        label: t('@astryx.powersearch.editor.groupOperator'),
        isLabelHidden: true,
        options: operatorOptions,
        value: partialFilter.operator,
        onChange: onOperatorChange,
        isDisabled: isReadOnly,
        size: "md"
      })
    }) : operatorOptions[0]?.label ?? t('@astryx.powersearch.editor.group')
  });
  const items = [{
    id: 'nested-root',
    label: rootLabel,
    isExpanded: true,
    children: treeChildren
  }];
  return /*#__PURE__*/_jsx(TreeList, {
    items: items,
    density: "balanced"
  });
}

// =============================================================================
// Main popover
// =============================================================================

export function PowerSearchEditPopover({
  config,
  filter: initialFilter,
  mode,
  onSave,
  onCancel,
  saveButtonLabel: saveButtonLabelFromProps,
  isReadOnly = false
}) {
  const t = useTranslator();
  const saveButtonLabel = saveButtonLabelFromProps ?? t('@astryx.powersearch.editor.apply');
  const [partialFilter, setPartialFilter] = useState(initialFilter);
  const valueEditorRef = useRef(null);

  // Focus the first focusable element inside the value editor after mount
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const container = valueEditorRef.current;
      if (!container) {
        return;
      }
      const focusable = container.querySelector('input:not([disabled]), button:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
      focusable?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, []);
  const currentOperator = partialFilter.operator ? config.getOperator(partialFilter.field, partialFilter.operator) : undefined;

  // Build field options for the selector
  const fieldOptions = useMemo(() => config.getVisibleFields().map(field => ({
    value: field.key,
    label: field.label
  })), [config]);

  // Build operator options for the current field
  const operatorOptions = useMemo(() => {
    const operators = config.getVisibleOperators(partialFilter.field);
    return operators.map(op => ({
      value: op.key,
      label: resolveOperatorLabel(op, t)
    }));
  }, [config, partialFilter.field, t]);
  const handleFieldChange = useCallback(fieldKey => {
    const defaultOp = config.getDefaultOperator(fieldKey);
    setPartialFilter({
      field: fieldKey,
      operator: defaultOp?.key,
      value: undefined
    });
  }, [config]);
  const handleOperatorChange = useCallback(operatorKey => {
    const newOp = config.getOperator(partialFilter.field, operatorKey);
    const oldOp = currentOperator;
    const keepValue = newOp && oldOp && newOp.value.type === oldOp.value.type;
    setPartialFilter(prev => ({
      ...prev,
      operator: operatorKey,
      value: keepValue ? prev.value : undefined
    }));
  }, [config, partialFilter.field, currentOperator]);
  const handleValueChange = useCallback((value, shouldSave) => {
    setPartialFilter(prev => {
      const updated = {
        ...prev,
        value
      };
      if (shouldSave && updated.field && updated.operator && updated.value) {
        onSave({
          field: updated.field,
          operator: updated.operator,
          value: updated.value
        });
      }
      return updated;
    });
  }, [onSave]);
  const handleSave = useCallback(() => {
    if (partialFilter.field && partialFilter.operator && partialFilter.value) {
      onSave({
        field: partialFilter.field,
        operator: partialFilter.operator,
        value: partialFilter.value
      });
    }
  }, [partialFilter, onSave]);
  const handleDelete = useCallback(() => {
    onSave(null);
  }, [onSave]);
  const isSaveDisabled = !partialFilter.operator || !partialFilter.value;

  // Handle Enter to save, Escape to cancel
  const handleKeyDown = useCallback(e => {
    // Don't treat an IME composition-commit Enter as save-to-close --
    // typing a CJK filter value and pressing Enter to confirm the
    // composition would otherwise close the popover mid-composition.
    if (isImeKeyEvent(e.nativeEvent)) {
      return;
    }
    if (e.key === 'Enter' && !isSaveDisabled && !e.defaultPrevented) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape' && !e.defaultPrevented) {
      e.preventDefault();
      onCancel();
    }
  }, [isSaveDisabled, handleSave, onCancel]);
  const operatorValue = currentOperator?.value;
  const isEmptyType = operatorValue?.type === 'empty';
  const isNestedType = operatorValue?.type === 'nested';

  // For empty type, auto-save on mount
  useEffect(() => {
    if (isEmptyType && partialFilter.field && partialFilter.operator) {
      onSave({
        field: partialFilter.field,
        operator: partialFilter.operator,
        value: {
          type: 'empty'
        }
      });
    }
  }, [isEmptyType, partialFilter.field, partialFilter.operator, onSave]);
  const showOperatorSelector = operatorOptions.length > 1 || !isEmptyType;

  // Nested filter editing
  if (isNestedType) {
    return /*#__PURE__*/_jsxs("div", {
      ...{
        className: "xb3r6kr"
      },
      onKeyDown: handleKeyDown,
      children: [/*#__PURE__*/_jsx("div", {
        ...{
          className: "x1shk3sm"
        },
        children: /*#__PURE__*/_jsxs(VStack, {
          gap: 2,
          children: [/*#__PURE__*/_jsx(HStack, {
            gap: 2,
            children: /*#__PURE__*/_jsx("div", {
              ...{
                className: "x1iyjqo2 xs83m0k xeuugli"
              },
              children: /*#__PURE__*/_jsx(Selector, {
                label: t('@astryx.powersearch.editor.field'),
                isLabelHidden: true,
                options: fieldOptions,
                value: partialFilter.field,
                onChange: handleFieldChange,
                isDisabled: isReadOnly,
                size: "md"
              })
            })
          }), /*#__PURE__*/_jsx(NestedEditor, {
            config: config,
            partialFilter: partialFilter,
            operatorOptions: operatorOptions,
            onOperatorChange: handleOperatorChange,
            onPartialFilterChange: setPartialFilter,
            isReadOnly: isReadOnly
          })]
        })
      }), /*#__PURE__*/_jsx("div", {
        ...{
          className: "x1b2ylru xexx8yu"
        },
        children: /*#__PURE__*/_jsxs(HStack, {
          gap: 2,
          hAlign: "between",
          children: [!isReadOnly && mode === 'edit' ? /*#__PURE__*/_jsx(Button, {
            label: t('@astryx.powersearch.editor.delete'),
            onClick: handleDelete,
            variant: "ghost",
            size: "sm"
          }) : /*#__PURE__*/_jsx("div", {}), /*#__PURE__*/_jsxs(HStack, {
            gap: 2,
            children: [/*#__PURE__*/_jsx(Button, {
              label: t('@astryx.powersearch.editor.cancel'),
              onClick: onCancel,
              variant: "ghost",
              size: "sm"
            }), /*#__PURE__*/_jsx(Button, {
              label: saveButtonLabel,
              onClick: handleSave,
              variant: "primary",
              size: "sm",
              isDisabled: isSaveDisabled
            })]
          })]
        })
      })]
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    ...{
      className: "xb3r6kr"
    },
    onKeyDown: handleKeyDown,
    children: [/*#__PURE__*/_jsx("div", {
      ...{
        className: "x1shk3sm"
      },
      children: /*#__PURE__*/_jsxs(HStack, {
        gap: 2,
        children: [/*#__PURE__*/_jsx("div", {
          ...{
            className: "x1iyjqo2 xs83m0k xeuugli"
          },
          children: /*#__PURE__*/_jsx(Selector, {
            label: t('@astryx.powersearch.editor.field'),
            isLabelHidden: true,
            options: fieldOptions,
            value: partialFilter.field,
            onChange: handleFieldChange,
            isDisabled: isReadOnly,
            size: "md"
          })
        }), showOperatorSelector && operatorOptions.length > 0 && /*#__PURE__*/_jsx("div", {
          ...{
            className: "x1iyjqo2 x2lah0s"
          },
          children: /*#__PURE__*/_jsx(Selector, {
            label: t('@astryx.powersearch.editor.operator'),
            isLabelHidden: true,
            options: operatorOptions,
            value: partialFilter.operator,
            onChange: handleOperatorChange,
            isDisabled: isReadOnly,
            size: "md"
          })
        }), operatorValue && !isEmptyType && /*#__PURE__*/_jsx("div", {
          ref: valueEditorRef,
          ...{
            className: "xgyuaek xeuugli"
          },
          children: /*#__PURE__*/_jsx(PowerSearchValueEditor, {
            operatorValue: operatorValue,
            filterValue: partialFilter.value,
            onChange: handleValueChange,
            onEnter: handleSave,
            config: config,
            isDisabled: isReadOnly
          })
        })]
      })
    }), !isEmptyType && /*#__PURE__*/_jsx("div", {
      ...{
        className: "x1b2ylru xexx8yu"
      },
      children: /*#__PURE__*/_jsxs(HStack, {
        gap: 2,
        hAlign: "between",
        children: [!isReadOnly && mode === 'edit' ? /*#__PURE__*/_jsx(Button, {
          label: t('@astryx.powersearch.editor.delete'),
          onClick: handleDelete,
          variant: "ghost",
          size: "sm"
        }) : /*#__PURE__*/_jsx("div", {}), /*#__PURE__*/_jsxs(HStack, {
          gap: 2,
          children: [/*#__PURE__*/_jsx(Button, {
            label: t('@astryx.powersearch.editor.cancel'),
            onClick: onCancel,
            variant: "ghost",
            size: "sm"
          }), /*#__PURE__*/_jsx(Button, {
            label: saveButtonLabel,
            onClick: handleSave,
            variant: "primary",
            size: "sm",
            isDisabled: isSaveDisabled
          })]
        })]
      })
    })]
  });
}